import type {
  WizardConfig,
  WizardResolvable,
  WizardStep,
  WizardStepCode,
  WizardStepDef,
  WizardStepDefExpand,
  WizardStepDefLeaf,
} from '@/wizard/types.ts';
import type { DotColor } from '@/modules/common/types/ui.ts';
import type { I18TParams } from '@/i18n.ts';
import { computed, ref, toValue, watch, type ComputedRef, type MaybeRefOrGetter, type Ref } from 'vue';
import { wizardStepKey } from '@/wizard/step-key.ts';

export type { WizardConfig, WizardStep, WizardStepCode } from '@/wizard/types.ts';

export type WizardHeader = {
  title: string | I18TParams;
  tooltip: string | I18TParams;
  dot?: DotColor;
};

export type WizardMenuItem = {
  key: string;
  title: string | I18TParams;
  badge?: number;
};

export type WizardOptions<TCode extends WizardStepCode = string> = {
  onReset?: () => void;
  close?: () => void;
  onComplete?: () => void | Promise<void>;
  isDirty?: (ctx: { page: Ref<number> }) => ComputedRef<boolean>;
  resolveNext?: (ctx: { page: number; steps: WizardStep<TCode>[] }) => number | undefined;
};

export type WizardContext<TCode extends WizardStepCode = string> = {
  page: Ref<number>;
  steps: ComputedRef<WizardStep<TCode>[]>;
  step: ComputedRef<WizardStep<TCode>>;
  isValid: Ref<boolean>;
  isDirty: ComputedRef<boolean>;
  showErrors: Ref<boolean>;
  isLoading: Ref<boolean>;
  header: ComputedRef<WizardHeader>;
  menu: ComputedRef<WizardMenuItem[]>;
  onlyNext: ComputedRef<boolean>;
  completeAsNext: ComputedRef<boolean>;
  completeAsClose: ComputedRef<boolean>;
  isFirstPage: ComputedRef<boolean>;
  isLastPage: ComputedRef<boolean>;
  canComplete: ComputedRef<boolean>;
  showForceClose: Ref<boolean>;
  goTo: (page: number) => void;
  back: () => void;
  next: () => void;
  close: () => void;
  clear: () => void;
  setShowForceClose: (show: boolean) => void;
  registerComplete: (handler?: () => void | Promise<void>) => void;
  complete: () => void | Promise<void>;
};

const ON_COMPLETE_NOT_DEFINED_ERROR = 'Обработчик onComplete не зарегистрирован.';
const NO_VISIBLE_STEPS_ERROR = 'В конфиге визарда нет видимых шагов.';

const isExpandStep = <TData, TCode extends WizardStepCode>(
  step: WizardStepDef<TData, TCode>
): step is WizardStepDefExpand<TData, TCode> => 'expand' in step;

const isVisible = <TData, TCode extends WizardStepCode>(step: WizardStepDef<TData, TCode>, data: TData) => {
  const { visible } = step;
  return visible === undefined ? true : typeof visible === 'function' ? visible(data) : visible;
};

const resolveValue = <T, TData>(value: WizardResolvable<T, TData>, data: TData): T =>
  typeof value === 'function' ? (value as (data: TData) => T)(data) : value;

const resolveLeafStep = <TData, TCode extends WizardStepCode>(
  step: WizardStepDefLeaf<TData, TCode>,
  data: TData
): WizardStep<TCode> => ({
  code: step.code,
  component: step.component,
  title: resolveValue(step.title, data),
  menuTitle: resolveValue(step.menuTitle, data),
  tooltip: resolveValue(step.tooltip, data),
  badge: step.badge?.(data),
  dot: step.dot ? resolveValue(step.dot, data) : undefined,
  index: step.index,
  onlyNext: step.navigation?.onlyNext,
  completeAsNext: step.navigation?.completeAsNext,
  completeAsClose: step.navigation?.completeAsClose,
  hideNext: step.navigation?.hideNext,
  ...(step.layout ? resolveValue(step.layout, data) : undefined),
});

const resolveStep = <TData, TCode extends WizardStepCode>(
  step: WizardStepDef<TData, TCode>,
  data: TData
): WizardStep<TCode>[] => {
  if (!isVisible(step, data)) {
    return [];
  }

  return isExpandStep(step)
    ? step.expand(data).map((child) => resolveLeafStep(child, data))
    : [resolveLeafStep(step, data)];
};

function resolveSteps<TData, TCode extends WizardStepCode = string>(
  config: WizardConfig<TData, TCode>,
  data: TData
): WizardStep<TCode>[] {
  return config.steps.flatMap((step) => resolveStep(step, data));
}

function clampPage(page: Ref<number>, stepCount: number) {
  const maxPage = Math.max(0, stepCount - 1);
  if (page.value > maxPage) {
    page.value = maxPage;
  }

  return maxPage;
}

function preservePage<TCode extends WizardStepCode>(
  page: Ref<number>,
  resolvedSteps: WizardStep<TCode>[],
  previousSteps: WizardStep<TCode>[]
) {
  const current = previousSteps[page.value];

  if (!current) {
    clampPage(page, resolvedSteps.length);
    return;
  }

  const nextPage = resolvedSteps.findIndex((step) => step.code === current.code && step.index === current.index);

  if (nextPage >= 0) {
    page.value = nextPage;
    return;
  }

  clampPage(page, resolvedSteps.length);
}

export function createWizard<TData, TCode extends WizardStepCode = string>(
  config: WizardConfig<TData, TCode>,
  data: MaybeRefOrGetter<TData>,
  options: WizardOptions<TCode> = {}
): WizardContext<TCode> {
  const page = ref(0);
  const isValid = ref(false);
  const showErrors = ref(false);
  const isLoading = ref(false);
  const showForceClose = ref(false);
  const persistentOnComplete = options.onComplete;
  const onComplete = ref<(() => void | Promise<void>) | undefined>(persistentOnComplete);
  const isDirty = options.isDirty?.({ page }) ?? computed(() => false);

  const steps = computed(() => resolveSteps(config, toValue(data)));

  watch(
    steps,
    (resolvedSteps, previousSteps) => {
      if (previousSteps?.length) {
        preservePage(page, resolvedSteps, previousSteps);
        return;
      }

      clampPage(page, resolvedSteps.length);
    },
    { flush: 'sync' }
  );

  const step = computed(() => {
    const resolvedSteps = steps.value;

    if (!resolvedSteps.length) {
      throw new Error(NO_VISIBLE_STEPS_ERROR);
    }

    return resolvedSteps[page.value] ?? resolvedSteps[resolvedSteps.length - 1];
  });
  const header = computed(() => ({
    title: step.value.title,
    tooltip: step.value.tooltip,
    dot: step.value.dot,
  }));
  const menu = computed(() =>
    steps.value.map((item) => ({
      key: wizardStepKey(item),
      title: item.menuTitle,
      badge: item.badge,
    }))
  );
  const isFirstPage = computed(() => page.value === 0);
  const isLastPage = computed(() => page.value === steps.value.length - 1);
  const onlyNext = computed(() => Boolean(step.value.onlyNext));
  const completeAsNext = computed(() => Boolean(step.value.completeAsNext));
  const completeAsClose = computed(() => Boolean(step.value.completeAsClose));
  const canComplete = computed(() => Boolean(onComplete.value));

  const registerComplete = (handler?: () => void | Promise<void>) => {
    onComplete.value = handler ?? persistentOnComplete;
  };

  const reset = () => {
    page.value = 0;
    isValid.value = false;
    showErrors.value = false;
    isLoading.value = false;
    showForceClose.value = false;
    onComplete.value = persistentOnComplete;
  };

  const goTo = (target: number) => {
    const maxPage = Math.max(0, steps.value.length - 1);
    const clampedTarget = Math.min(Math.max(0, target), maxPage);

    if (clampedTarget === page.value) {
      return;
    }

    if (clampedTarget < page.value) {
      page.value = clampedTarget;
      return;
    }

    const isAllowed = !isLoading.value && isValid.value;
    showErrors.value = !isAllowed;

    if (isAllowed) {
      page.value = clampedTarget;
      isValid.value = false;
      showErrors.value = false;
    }
  };

  const complete = () => {
    if (!onComplete.value) {
      throw new Error(ON_COMPLETE_NOT_DEFINED_ERROR);
    }

    return onComplete.value();
  };

  return {
    page,
    steps,
    step,
    isValid,
    isDirty,
    showErrors,
    isLoading,
    header,
    menu,
    onlyNext,
    completeAsNext,
    completeAsClose,
    isFirstPage,
    isLastPage,
    canComplete,
    showForceClose,
    goTo,
    back: () => goTo(page.value - 1),
    next: () => {
      const target = options.resolveNext?.({ page: page.value, steps: steps.value });
      goTo(typeof target === 'number' ? target : page.value + 1);
    },
    close: () => options.close?.(),
    clear: () => {
      options.onReset?.();
      options.close?.();
      reset();
    },
    setShowForceClose: (show) => {
      showForceClose.value = show;
    },
    registerComplete,
    complete,
  };
}
